/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useFormik } from 'formik';
import localforage from 'localforage';
import isEqual from 'lodash/isEqual';
import uniqWith from 'lodash/uniqWith';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { Detector } from 'react-detect-offline';
import NumberFormat from 'react-number-format';
import { useHistory, useLocation, useParams } from 'react-router';
import { AsyncPaginate } from 'react-select-async-paginate';

import type { QueryParamProductConfig } from '../../api/GET_ProductConfig';
import { getProductConfig } from '../../api/GET_ProductConfig';
import { postDeleteImages } from '../../api/POST_DeleteImages';
import { postValidateMaxAmount } from '../../api/POST_ValidateMaxAmount';
import { GlobalContext } from '../../App';
import { BodyWrapper } from '../../components/BodyWrapper';
import Button from '../../components/Button';
import DropdownIndicator from '../../components/DropdownIndicator';
import Header from '../../components/Header';
import Layout from '../../components/Layout';
import ModalAccountInfo from '../../components/ModalAccountInfo';
import ModalCalendar from '../../components/ModalCalendar';
import ModalConfirmation from '../../components/ModalConfirmation';
import ModalUploadImage from '../../components/ModalUploadImage';
import { LocalStorageKeyEnum } from '../../enums/LocalStorageKey';
import { bulkDeleteImages } from '../../utils/bulkDeleteImages';
import { customStyles } from '../../utils/customStyles';
import { dateFormatter } from '../../utils/dateFormatter';
import { ParamsCategory, ProductForm, ProductInterface, User } from './index.type';

function useQuery(): any {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const AddProduct = () => {
  const globalContext = useContext(GlobalContext);

  const cancelButtonRef = React.useRef(null);
  const { category }: ParamsCategory = useParams();
  const query = useQuery();
  const history = useHistory();

  const { handleSubmit, values, setFieldValue, resetForm } = useFormik<ProductForm>({
    initialValues: {
      user: null,
      productsList: [],
      products: [
        {
          product: null,
          count: null,
          images: null,
          date: null
        }
      ],
      isModalAccountVisible: false,
      isModalPromptVisible: false,
      isModalCalendarVisible: false,
      isModalUploadImageVisible: false,
      isModalConfirmationVisible: false,
      isOnline: false,
      isValid: false,
      currentIndex: 0,
      sku: '',
      brandName: '',
      currentImages: null,
      onSearchMessage: null,
      isLoading: false
    },
    onSubmit: async (formValues) => {
      const oldProduct = await localforage.getItem(LocalStorageKeyEnum['PRODUCTS']);

      if (oldProduct) {
        oldProduct[category] = {
          products: formValues.products
        };
        await localforage.setItem(LocalStorageKeyEnum['PRODUCTS'], oldProduct);
        globalContext.setProducts((prev) => {
          prev[category] = {
            products: formValues.products
          };

          return prev;
        });
      } else {
        const parsed = {};
        parsed[category] = {
          products: formValues.products
        };
        await localforage.setItem(LocalStorageKeyEnum['PRODUCTS'], parsed);

        globalContext.setProducts((prev) => {
          prev[category] = {
            products: formValues.products
          };

          return prev;
        });
      }

      history.goBack();
    }
  });

  const fetchProductConfig = useCallback(
    async (params?: QueryParamProductConfig) => {
      try {
        const productConfig = await getProductConfig(query.get('id'), params);
        const options = productConfig?.data?.length
          ? productConfig?.data?.map((item: any) => ({
              label: `[${item.detailProduct[0].code}] ${item.detailProduct[0].name}`,
              value: {
                productId: item.productId,
                brandId: item.detailProduct[0].brandId,
                brandName: item.detailProduct[0].brandName,
                sku: item.detailProduct[0].code,
                uomId: item.detailUomCategory[0].id,
                uom: item.detailUomCategory[0].uom,
                name: item.detailProduct[0].name,
                maxQty: item?.qtyConfig?.maxQty || 0
              }
            }))
          : [];
        return { options, paginate: productConfig?.paginate };
      } catch (error) {
        throw new Error(error?.message || 'Failed fetch product config');
      }
    },
    [query]
  );

  useEffect(() => {
    if (window) {
      localforage.getItem(LocalStorageKeyEnum['PRODUCTS'], (_, products) => {
        if (!products || !products[category]?.products) return;
        setFieldValue('products', products[category].products);
      });
    } else {
      if (Object.keys(globalContext.products).includes(category)) {
        setFieldValue('products', globalContext.products[category].products);
      }
    }
  }, []);

  const addProduct = () => {
    const products = JSON.parse(JSON.stringify(values.products));

    if (
      category === 'Produk Kadaluarsa' ||
      category === 'Return to Brand Partner' ||
      category === 'Return to Hub Kemang'
    ) {
      if (
        products[values.currentIndex].product &&
        products[values.currentIndex].count &&
        products[values.currentIndex].images &&
        products[values.currentIndex].date
      ) {
        products.unshift({ product: null, count: null, images: null, date: null });
        setFieldValue('products', products);
        setFieldValue('isValid', false);
      }
    } else {
      if (products[values.currentIndex].product && products[values.currentIndex].count) {
        if (isNoImage || (!isNoImage && products[values.currentIndex].images)) {
          products.unshift({ product: null, count: null, images: null, date: null });
          setFieldValue('products', products);
          setFieldValue('isValid', false);
        }
      }
    }
  };

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/^[0-9.,]*$/.test(event.key)) {
      event.preventDefault();
    }
  };

  const handleProductChange = (
    index: number,
    value: any,
    type: 'product' | 'count' | 'images' | 'date'
  ) => {
    const products: typeof values.products = JSON.parse(JSON.stringify(values.products));
    switch (type) {
      case 'product':
        if (products[index].product?.productId !== value?.productId) {
          const productReset = resetProduct(index);
          products[index] = productReset;
        }
        products[index].product = value;
        break;
      case 'count':
        products[index].count = value;
        break;
      case 'images':
        products[index].images = value?.length > 0 ? value : null;
        break;
      case 'date':
        products[index].date = value;
        break;
    }

    setFieldValue('products', products);
  };

  const closeModalBackground = () => undefined;
  const toggleModalAccountVisible = () =>
    setFieldValue('isModalAccountVisible', !values.isModalAccountVisible);
  const toggleModalPrompt = () =>
    setFieldValue('isModalPromptVisible', !values.isModalPromptVisible);
  const toggleModalUploadImage = () =>
    setFieldValue('isModalUploadImageVisible', !values.isModalUploadImageVisible);
  const toggleModalCalendar = () =>
    setFieldValue('isModalCalendarVisible', !values.isModalCalendarVisible);

  const onClickPrompt = () => {
    discardProducts();
    resetForm();
    history.push('/');
  };

  const discardProducts = React.useCallback(async () => {
    const user = await localforage.getItem<User>(LocalStorageKeyEnum['USER']);
    const newCategories: Array<ProductInterface> = user.categories
      .filter((item) => item.name === category)
      .map((item) => item.products)
      .flat();
    const discardProduct: Array<ProductInterface> = values.products.filter(
      (value) => !newCategories.some((value2) => value.product.name === value2.product.name)
    );
    if (discardProduct.length > 0) {
      await bulkDeleteImages(discardProduct);
    }
  }, [category, values.products]);

  const getUser = React.useCallback(async () => {
    const userLocalStorage = await localforage.getItem<User>(LocalStorageKeyEnum['USER']);
    if (userLocalStorage) {
      // const categories = user.categories;
      // const filterCategories = categories.filter((item) => item.name === category);
      // if (filterCategories[0].products[0]?.product) {
      //   setFieldValue('products', filterCategories[0].products);
      // }
      setFieldValue('user', userLocalStorage);
    }
  }, [setFieldValue]);

  React.useEffect(() => {
    getUser();
  }, [getUser]);

  React.useEffect(() => {
    const checker = values.products.filter((product) => {
      let check =
        !product.count ||
        !product.count.quantity ||
        product.count.isMaxAmount ||
        !product.product ||
        ((category === 'Produk Kadaluarsa' ||
          category === 'Return to Brand Partner' ||
          category === 'Return to Hub Kemang') &&
          !product.date);

      if (!isNoImage) {
        check = check || !product.images;
      }

      return check;
    });
    if (checker.length > 0) {
      setFieldValue('isValid', false);
    } else {
      setFieldValue('isValid', true);
    }
  }, [setFieldValue, values]);

  React.useEffect(() => {
    const beforeUnloadEvent = () => {
      if (values.products[values.currentIndex].images) {
        discardProducts();
      }
      resetForm();
    };
    window.addEventListener('beforeunload', beforeUnloadEvent);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadEvent);
    };
  }, [values.products, values.currentIndex, resetForm, discardProducts]);

  React.useEffect(() => {
    const onBackButtonEvent = () => history.push('/');

    window.addEventListener('popstate', onBackButtonEvent);
    return () => {
      window.removeEventListener('popstate', onBackButtonEvent);
    };
  }, [history]);

  const onModalUploadImage = (
    index: number,
    sku: string,
    brandName: string,
    images: Array<{ uuid: string; dataURL: string }> | null
  ) => {
    setFieldValue('currentIndex', index);
    setFieldValue('sku', sku);
    setFieldValue('brandName', brandName);
    if (images) {
      setFieldValue('currentImages', images);
    }
    toggleModalUploadImage();
  };

  const loadOptions = useCallback(
    async (searchQuery, loadedOptions, additional) => {
      const { options, paginate } = await fetchProductConfig({
        filter: { searchKeyword: searchQuery },
        paginate: { offset: additional.offset, limit: 10 }
      });

      if (!options.length) {
        setFieldValue('onSearchMessage', 'Produk tidak dapat ditemukan');
      }

      const allOptions = Array.isArray(loadedOptions) ? loadedOptions.concat(options) : options;
      const uniqueAllOptions = uniqWith(allOptions, isEqual);
      const hasMore = uniqueAllOptions.length < paginate.total;

      return {
        options,
        hasMore: hasMore,
        additional: {
          offset: hasMore ? additional.offset + 10 : additional.offset
        }
      };
    },
    [fetchProductConfig, setFieldValue]
  );

  const onValidateMaxAmount = (amount: string, uomId: number, index: number) => {
    const products = JSON.parse(JSON.stringify(values.products));

    if (amount) {
      const body = {
        amount: parseInt(amount),
        uomId
      };
      postValidateMaxAmount(body).then(({ status }) => {
        if (status === 'success') {
          products[index].count.isMaxAmount = false;
          setFieldValue('products', products);
        } else {
          products[index].count.isMaxAmount = true;
          setFieldValue('products', products);
        }
      });
    }
  };

  const onCloseModal = () => {
    toggleModalUploadImage();
  };

  const deleteProduct = async (index: number, images: Array<{ uuid: string; dataURL: string }>) => {
    const products = values.products.filter((prod) => prod !== values.products[index]);
    const uuidImages = images?.map((image) => image.uuid);
    if (uuidImages) {
      await postDeleteImages({ uuid: uuidImages });
    }
    setFieldValue('products', products);
  };

  const resetProduct = (index: number): ProductInterface => {
    const uuidImages = values.products[index].images?.map((image) => image.uuid);
    if (uuidImages) {
      postDeleteImages({ uuid: uuidImages });
    }
    const products = JSON.parse(JSON.stringify(values.products));
    products[index] = { ...products[index], count: null, images: null };
    return products[index];
  };

  const onChangeCount = (e, index) => {
    const count = e.target.value
      ? {
          quantity: e.target.value,
          isMaxAmount: false
        }
      : null;

    if (+count?.quantity > 99999) {
      count.quantity = '99.999';
    } else if (count?.quantity[0] === '0') {
      count.quantity = null;
    }

    if (count !== null) {
      if (values.products[index].product.maxQty < parseInt(count.quantity)) {
        const product_ = [...values.products];
        const obj = { ...values.products[index] };
        obj.count = {
          ...count,
          isMaxAmount: e.target.value > 99999 ? true : true
        };
        product_[index] = obj;
        setFieldValue('products', product_);
      } else {
        const product_ = [...values.products];
        const obj = { ...values.products[index] };
        obj.count = {
          ...count,
          isMaxAmount: e.target.value > 99999 ? true : false
        };
        product_[index] = obj;
        setFieldValue('products', product_);
      }
    }

    //handleProductChange(index, count, 'count');
  };

  const checkNetwork = () => (
    <Detector
      render={({ online }) => {
        if (!online) {
          return (
            <div className="flex justify-center items-center bg-danger-default w-full h-9">
              <div className="text-regular text-white font-interLight">
                Tidak ada koneksi internet
              </div>
            </div>
          );
        } else {
          if (values.isOnline) {
            return (
              <div className="flex justify-center items-center bg-positive-lighter w-full h-9">
                <div className="text-regular text-primary-green font-interLight">
                  Koneksi kembali stabil
                </div>
              </div>
            );
          }
          return <div />;
        }
      }}
      onChange={(value: boolean) => {
        if (value) {
          setFieldValue('isOnline', true);
          setTimeout(() => setFieldValue('isOnline', false), 3000);
        }
      }}
    />
  );

  const onBlur = () => setFieldValue('onSearchMessage', null);
  const isNoImage = useMemo(() => {
    const excludeCategoryImages = ['outbound'];
    return excludeCategoryImages.some((excludeCat) => category.toLowerCase().includes(excludeCat));
  }, [category]);

  return (
    <div className="min-h-screen bg-gray-gray200 bg-opacity-100">
      <ModalUploadImage
        product={values.products[values.currentIndex]}
        outletName={values.user?.outletName}
        brandName={values.brandName}
        cancelButtonRef={cancelButtonRef}
        currentIndex={values.currentIndex}
        category={category}
        sku={values.sku}
        handleProductChange={handleProductChange}
        showModal={values.isModalUploadImageVisible}
        closeModal={onCloseModal}
        closeModalBackground={() => undefined}
      />
      <ModalAccountInfo
        outletName={values.user?.outletName}
        outletCrew={values.user?.name}
        dateInput={`${dateFormatter(
          new Date(values.user && values.user?.startTime),
          'EEEE, dd MMMM yyyy'
        )} / ${dateFormatter(new Date(values.user && values.user?.startTime), 'HH:mm')} WIB`}
        cancelButtonRef={cancelButtonRef}
        isShowModal={values.isModalAccountVisible}
        closeModal={toggleModalAccountVisible}
        closeModalBackground={closeModalBackground}
      />
      <ModalConfirmation
        icon="/images/warning.svg"
        title="Tinggalkan Halaman Ini?"
        description="Perubahan yang belum disimpan akan dihapus. Yakin lanjutkan?"
        textLeft="Batal"
        textRight="Ya, lanjutkan"
        onClick={onClickPrompt}
        cancelButtonRef={cancelButtonRef}
        isShowModal={values.isModalPromptVisible}
        closeModal={toggleModalPrompt}
        closeModalBackground={closeModalBackground}
        variantButton="DOUBLE"
      />
      <ModalCalendar
        currentIndex={values.currentIndex}
        startDate={values.products[values.currentIndex].date}
        handleProductChange={handleProductChange}
        title="Tanggal Expired"
        description="Pilih tanggal expired untuk produk ini"
        cancelButtonRef={cancelButtonRef}
        isShowModal={values.isModalCalendarVisible}
        closeModal={toggleModalCalendar}
        closeModalBackground={closeModalBackground}
      />
      <Header title="Waste Form" showModalConfirmation={toggleModalPrompt} canGoback>
        <div className="w-full bg-white sticky top-15 mt-1 shadow-md">
          <div className="flex justify-between border-b border-gray-gray200 py-2 px-4">
            <div className="flex justify-center items-center cursor-pointer">
              <img src="/images/circleInfo.svg" className="w-4 h-4 mr-1" alt="Info icon" />
              <div
                onClick={toggleModalAccountVisible}
                className="text-regular text-primary-green font-interSemiBold">
                Info Form
              </div>
            </div>
            <div
              onClick={addProduct}
              className="flex border py-1.5 px-5 rounded-md border-primary-green bg-white">
              <div className="text-regular text-primary-green font-interSemiBold mr-1">
                Tambah Produk
              </div>
              <img src="/images/plus.svg" alt="Plus icon" />
            </div>
          </div>
          <div className="flex border-b border-gray-gray200 py-2 px-4">
            <div className="text-regular text-gray-gray700 font-interLight mr-1">Kategori:</div>
            <div className="text-regular text-gray-gray700 font-interSemiBold">{category}</div>
          </div>
        </div>
        <div className="w-full sticky top-36">{checkNetwork()}</div>
      </Header>
      <Layout>
        <BodyWrapper>
          <form>
            {values.products.map((item, index) => (
              <div key={index} className="bg-white p-2 rounded-lg shadow-md mb-3">
                <div className="grid grid-cols-12 gap-1 mb-2">
                  {values.products?.length > 1 && (
                    <div
                      onClick={() => deleteProduct(index, item.images)}
                      className="flex flex-col col-span-1 pt-2 m-auto">
                      <img className="my-auto" src="/images/trash.svg" alt="Trash Icon" />
                    </div>
                  )}
                  <div className="flex flex-col col-span-7">
                    <div className="font-interSemiBold text-small uppercase text-left text-gray-gray600 mb-0.5">
                      Nama Produk
                    </div>
                    <AsyncPaginate
                      placeholder="Cari Produk"
                      components={{ DropdownIndicator, IndicatorSeparator: null }}
                      isFocused={false}
                      className="w-auto max-h-18"
                      closeMenuOnSelect
                      debounceTimeout={1000}
                      onBlur={onBlur}
                      isError={!item.product}
                      styles={customStyles}
                      noOptionsMessage={() => values.onSearchMessage}
                      additional={{ offset: 0 }}
                      loadOptions={loadOptions}
                      value={[
                        {
                          label: item.product?.name,
                          value: {
                            productId: item.product?.productId,
                            brandId: item.product?.brandId,
                            brandName: item.product?.brandName,
                            sku: item.product?.sku
                          }
                        }
                      ]}
                      onChange={(e: {
                        label: string;
                        value: {
                          brandId: number;
                          productId: number;
                          sku: string;
                          brandName: string;
                          uom: string;
                          uomId: number;
                          name: string;
                        };
                      }) => {
                        handleProductChange(index, e.value, 'product');
                      }}
                    />
                  </div>
                  <div
                    className={
                      values.products?.length > 1
                        ? 'flex flex-col col-span-2'
                        : 'flex flex-col col-span-3'
                    }>
                    <div className="font-interSemiBold text-small whitespace-nowrap text-left text-gray-gray600 mb-0.5 uppercase">
                      Jumlah
                    </div>
                    <div
                      className={`${
                        !item.product && !item.count
                          ? 'w-full h-full flex justify-center items-center bg-gray-gray300 border outline-none rounded-lg font-interLight relative p-2'
                          : item.product && !item.count
                          ? 'w-full h-full flex flex-col justify-center border border-danger-default outline-none rounded-lg font-interLight py-1'
                          : 'w-full h-full flex flex-col justify-center items-center border border-gray-gray400 outline-none rounded-lg font-interLight py-1'
                      } m-auto`}>
                      <NumberFormat
                        type="text"
                        decimalSeparator=","
                        allowNegative={false}
                        placeholder={!item.product ? '0' : ''}
                        onChange={(e) => onChangeCount(e, index)}
                        onBlur={(e) =>
                          //onValidateMaxAmount(e.target.value, item.product.uomId, index)
                          onChangeCount(e, index)
                        }
                        value={item.count?.quantity || ''}
                        className="w-full outline-none bg-transparent text-right text-regular font-interLight text-gray-gray700 px-1"
                        disabled={!item.product}
                        onKeyPress={(event) => onKeyPress(event)}
                      />
                      {item.product && (
                        <div
                          className={
                            'text-regular w-full font-interLight text-gray-gray700 text-right px-1'
                          }>
                          {item.product.uom}
                        </div>
                      )}
                    </div>
                  </div>
                  {!isNoImage ? (
                    <div
                      onClick={
                        (item.product && item.count && !item.count.isMaxAmount) || item.images
                          ? () =>
                              onModalUploadImage(
                                index,
                                item.product?.sku,
                                item.product?.brandName,
                                item.images
                              )
                          : () => undefined
                      }
                      className="flex flex-col col-span-2 m-auto">
                      {item.images ? (
                        <img
                          className="w-8 mt-2 mx-auto bg-primary-green rounded-lg"
                          src="/images/landscapeImage.svg"
                          alt="Add image icon"
                        />
                      ) : item.product && item.count && !item.count.isMaxAmount ? (
                        <img
                          className="w-10 p-1 mt-2 mx-auto bg-danger-default rounded-lg"
                          src="/images/addImageWhite.svg"
                          alt="Add image icon"
                        />
                      ) : (
                        <img
                          className="w-10 p-1 mt-2 mx-auto bg-gray-gray300 rounded-lg"
                          src="/images/addImage.svg"
                          alt="Add image icon"
                        />
                      )}
                    </div>
                  ) : null}
                </div>
                {item.count?.isMaxAmount && (
                  <div className="flex mb-2">
                    <img src="/images/warning.svg" className="mr-2" />
                    <div className="text-small text-warning-default font-interSemiBold">
                      Hasil hitung melebihi batas maks
                    </div>
                  </div>
                )}
                {(category === 'Produk Kadaluarsa' ||
                  category === 'Return to Brand Partner' ||
                  category === 'Return to Hub Kemang') && (
                  <div className="flex relative">
                    <div className="text-regular text-gray-gray700 font-interLight mr-1">
                      Tanggal expired:
                    </div>
                    <div
                      onClick={() => {
                        setFieldValue('currentIndex', index);
                        toggleModalCalendar();
                      }}
                      className="flex">
                      <div
                        className={
                          item.date
                            ? 'text-regular text-gray-gray700 font-interSemiBold mr-1'
                            : 'text-regular text-danger-default font-interSemiBold mr-1'
                        }>
                        {item.date
                          ? `${dateFormatter(
                              new Date(item.date?.year, item.date?.month - 1, item.date?.day),
                              'dd MMMM yyyy'
                            )}`
                          : 'Pilih Tanggal'}
                      </div>
                      {item.date ? (
                        <img src="/images/chevronDownBlack.svg" />
                      ) : (
                        <img src="/images/chevronDownRed.svg" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </form>
        </BodyWrapper>
        <div className="fixed bottom-0 w-full p-4 bg-white">
          <Button
            isDisabled={values.isValid ? false : true}
            onClick={handleSubmit}
            label="Simpan"
          />
        </div>
      </Layout>
    </div>
  );
};
export default AddProduct;
